import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { ExamInstance, ExamInstanceUpdateBody } from 'src/app/models/exam_instance.model';
import { SkillQuestion } from 'src/app/models/skill_question.model';
import { ApiService } from 'src/app/services';

@Component({
  selector: 'app-exam-instance-items-editor',
  templateUrl: './exam-instance-items-editor.component.html',
  styleUrl: './exam-instance-items-editor.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule, MatSelectModule],
})
export class ExamInstanceItemsEditorComponent {
  private readonly api_service = inject(ApiService);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly form_builder = inject(FormBuilder);

  readonly exam_instance = input.required<ExamInstance>();
  readonly exam_instance_id = input.required<number>();

  readonly saved = output<void>();

  readonly loading = signal(false);
  readonly skill_questions = signal<SkillQuestion[]>([]);
  readonly selected_skill_question_id = signal<number | null>(null);

  readonly form = this.form_builder.group({
    name: ['', [Validators.required, Validators.maxLength(255)]],
    items: this.form_builder.array([]),
  });

  readonly available_skill_questions = computed(() => {
    const used_skill_question_ids = new Set(
      this.items_form_array.controls.map(item_control => item_control.get('skill_question_id')?.value as number)
    );

    return this.skill_questions().filter(skill_question => !used_skill_question_ids.has(skill_question.id));
  });

  readonly total_minutes_preview = computed(() => {
    const skill_questions_by_id = new Map(this.skill_questions().map(skill_question => [skill_question.id, skill_question]));

    return this.items_form_array.controls.reduce((total_minutes, item_control) => {
      const skill_question_id = item_control.get('skill_question_id')?.value as number;
      const skill_question = skill_questions_by_id.get(skill_question_id);
      const item_minutes = skill_question?.approximate_minutes ?? 0;

      return total_minutes + item_minutes;
    }, 0);
  });

  constructor() {
    this.loadSkillQuestions();

    effect(() => {
      const exam_instance = this.exam_instance();
      this.populateFormFromExamInstance(exam_instance);
    });
  }

  get items_form_array(): FormArray {
    return this.form.controls.items;
  }

  getSkillQuestionLabel(skill_question_id: number): string {
    const skill_question = this.skill_questions().find(sq => sq.id === skill_question_id);

    if (!skill_question) {
      return `#${skill_question_id}`;
    }

    const body_excerpt = skill_question.body.length > 80 ? `${skill_question.body.slice(0, 80)}…` : skill_question.body;
    const minutes_suffix = skill_question.approximate_minutes !== null ? ` · ${skill_question.approximate_minutes} min` : '';

    return `#${skill_question.id} — ${body_excerpt}${minutes_suffix}`;
  }

  getSkillQuestionMinutesLabel(skill_question_id: number): string {
    const skill_question = this.skill_questions().find(sq => sq.id === skill_question_id);
    const minutes = skill_question?.approximate_minutes;

    if (minutes === null || minutes === undefined) {
      return '—';
    }

    return `${minutes} min`;
  }

  addItemFromSelector(): void {
    const skill_question_id = this.selected_skill_question_id();

    if (skill_question_id === null) {
      return;
    }

    this.items_form_array.push(
      this.form_builder.group({
        skill_question_id: [skill_question_id, Validators.required],
        weight: [1, [Validators.required, Validators.min(0.01)]],
      })
    );

    this.selected_skill_question_id.set(null);
  }

  removeItem(item_index: number): void {
    this.items_form_array.removeAt(item_index);
  }

  moveItemUp(item_index: number): void {
    if (item_index <= 0) {
      return;
    }

    const item_control = this.items_form_array.at(item_index);
    this.items_form_array.removeAt(item_index);
    this.items_form_array.insert(item_index - 1, item_control);
  }

  moveItemDown(item_index: number): void {
    if (item_index >= this.items_form_array.length - 1) {
      return;
    }

    const item_control = this.items_form_array.at(item_index);
    this.items_form_array.removeAt(item_index);
    this.items_form_array.insert(item_index + 1, item_control);
  }

  onSkillQuestionToAddSelected(skill_question_id: number | null): void {
    this.selected_skill_question_id.set(skill_question_id);
  }

  save(): void {
    if (this.loading() || this.form.invalid) {
      return;
    }

    if (this.items_form_array.length === 0) {
      this.snack_bar.open('Añade al menos una pregunta al examen.', undefined, { duration: 4000 });
      return;
    }

    this.loading.set(true);

    const raw = this.form.getRawValue();
    const body: ExamInstanceUpdateBody = {
      name: raw.name ?? '',
      items: this.items_form_array.controls.map((item_control, item_index) => {
        const item_value = item_control.getRawValue() as { skill_question_id: number; weight: number };

        return {
          skill_question_id: item_value.skill_question_id,
          weight: item_value.weight ?? 1,
          sort_order: item_index,
        };
      }),
    };

    this.api_service
      .updateExamInstance(this.exam_instance_id(), body)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          this.loading.set(false);
          this.snack_bar.open('Examen actualizado', undefined, { duration: 3000 });
          this.saved.emit();
        },
        error: (err: { error?: { error?: string } }): void => {
          this.loading.set(false);
          const message = err?.error?.error ?? 'Error al guardar el examen.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
        },
      });
  }

  private loadSkillQuestions(): void {
    this.api_service
      .getSkillQuestions({ per_page: 500, order_by: 'id:desc' })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response: ArrayResponse<SkillQuestion> | SkillQuestion[]): void => {
          this.skill_questions.set(Array.isArray(response) ? response : response.data);
        },
      });
  }

  private populateFormFromExamInstance(exam_instance: ExamInstance): void {
    const sorted_items = [...(exam_instance.exam_instance_items ?? [])].sort(
      (first_item, second_item) => first_item.sort_order - second_item.sort_order
    );

    this.form.patchValue({ name: exam_instance.name });
    this.items_form_array.clear();

    sorted_items.forEach(exam_instance_item => {
      if (exam_instance_item.skill_question_id === null) {
        return;
      }

      this.items_form_array.push(
        this.form_builder.group({
          skill_question_id: [exam_instance_item.skill_question_id, Validators.required],
          weight: [exam_instance_item.weight, [Validators.required, Validators.min(0.01)]],
        })
      );
    });
  }
}
